import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Header, Footer } from "../components/Orchestrio";
import { trackPricingCtaClick, trackBookCallClick } from "../utils/analytics";

const PricingPage = () => {
  const handlePricingCtaClick = (tierName, ctaText) => {
    trackPricingCtaClick(tierName?.toLowerCase(), ctaText);
    if (ctaText?.toLowerCase().includes('call')) {
      trackBookCallClick('pricing_page');
    }
  };

  const packages = [
    {
      name: "Marketing Website",
      description: "For businesses that need a professional web presence",
      price: "5,000 – 15,000",
      timeline: "4-8 weeks",
      includes: [
        "Custom design (Figma)",
        "Responsive development",
        "Content management system",
        "SEO foundation",
        "Analytics setup",
        "30-day support"
      ],
      notIncluded: [
        "Copywriting",
        "Photography",
        "Ongoing hosting"
      ]
    },
    {
      name: "Web Application",
      description: "Custom software with proper architecture",
      price: "15,000 – 50,000+",
      timeline: "8-16 weeks",
      includes: [
        "Requirements & specification",
        "UI/UX design",
        "Frontend & backend development",
        "Database design",
        "API integrations",
        "Testing & QA",
        "Documentation",
        "Deployment",
        "90-day warranty"
      ],
      notIncluded: [
        "Ongoing hosting",
        "Third-party licenses"
      ],
      featured: true
    },
    {
      name: "System Integration",
      description: "Connect your existing tools and automate workflows",
      price: "3,000 – 15,000",
      timeline: "2-6 weeks",
      includes: [
        "System audit",
        "Integration architecture",
        "API development",
        "Data migration (if needed)",
        "Error handling",
        "Monitoring setup",
        "Documentation"
      ],
      notIncluded: [
        "Third-party API costs"
      ]
    }
  ];

  const maintenance = {
    name: "Ongoing Support",
    description: "Keep your systems running smoothly",
    tiers: [
      {
        name: "Essential",
        price: "500",
        hours: "Up to 4 hours/month",
        includes: ["Security updates", "Uptime monitoring", "Email support"]
      },
      {
        name: "Standard",
        price: "1,200",
        hours: "Up to 10 hours/month",
        includes: ["Everything in Essential", "Bug fixes", "Minor changes", "Phone support"]
      },
      {
        name: "Premium",
        price: "2,500",
        hours: "Up to 20 hours/month",
        includes: ["Everything in Standard", "Feature development", "Priority response", "Quarterly reviews"]
      }
    ]
  };

  const faqs = [
    {
      q: "How do you calculate project costs?",
      a: "We estimate based on scope and complexity, not hours. After a discovery phase, you get a fixed price that won't change unless you change the requirements."
    },
    {
      q: "What if my project is bigger than these ranges?",
      a: "No problem. These are typical ranges, not limits. Get in touch and we'll scope it properly."
    },
    {
      q: "Do you require a deposit?",
      a: "Yes. We typically ask for 40% upfront, 30% at midpoint, and 30% on delivery."
    },
    {
      q: "What's not included in these prices?",
      a: "Third-party costs (hosting, domains, SaaS subscriptions), stock imagery, and copywriting are usually separate unless specified."
    },
    {
      q: "Can I pay monthly?",
      a: "For larger projects, we can discuss milestone-based payments. For maintenance, it's monthly in advance."
    }
  ];

  return (
    <div className="min-h-screen bg-[#1E1E1E]" data-testid="pricing-page">
      <Header />

      {/* Header */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20">
        <div className="max-w-5xl mx-auto px-6">
          <p className="text-[#5F8FB8] text-sm font-medium tracking-wide mb-6">
            Pricing
          </p>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
            Clear, honest pricing
          </h1>
          <p className="text-lg text-[#9CA3AF] max-w-2xl leading-relaxed">
            Typical investment ranges for the systems we build. 
            Every project gets a fixed-price quote after discovery.
          </p>
        </div>
      </section>

      {/* Project Packages */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">Project packages</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((pkg, index) => (
              <div 
                key={index}
                className={`p-6 bg-[#242424] border ${pkg.featured ? 'border-[#2F5D8C]' : 'border-[#2E2E2E]'}`}
              >
                {pkg.featured && (
                  <span className="text-[#5F8FB8] text-xs font-medium uppercase tracking-wide">
                    Most common
                  </span>
                )}
                <h3 className="text-lg font-bold mt-2 mb-2">{pkg.name}</h3>
                <p className="text-sm text-[#9CA3AF] mb-4">{pkg.description}</p>
                
                <div className="mb-6">
                  <span className="text-2xl font-bold">£{pkg.price}</span>
                  <span className="text-[#6B7280] text-sm"> +VAT</span>
                  <p className="text-sm text-[#6B7280] mt-1">{pkg.timeline}</p>
                </div>

                <div className="mb-6">
                  <p className="text-sm font-medium mb-3">Includes:</p>
                  <ul className="space-y-2">
                    {pkg.includes.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                        <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {pkg.notIncluded && pkg.notIncluded.length > 0 && (
                  <div className="pt-4 border-t border-[#2E2E2E]">
                    <p className="text-xs text-[#6B7280] mb-2">Not included:</p>
                    <p className="text-xs text-[#6B7280]">{pkg.notIncluded.join(", ")}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Maintenance */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-2">{maintenance.name}</h2>
            <p className="text-[#9CA3AF]">{maintenance.description}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {maintenance.tiers.map((tier, index) => (
              <div key={index} className="p-6 bg-[#1E1E1E] border border-[#2E2E2E]">
                <h3 className="font-bold mb-2">{tier.name}</h3>
                <div className="mb-4">
                  <span className="text-2xl font-bold">£{tier.price}</span>
                  <span className="text-[#6B7280] text-sm">/month</span>
                </div>
                <p className="text-sm text-[#5F8FB8] mb-4">{tier.hours}</p>
                <ul className="space-y-2">
                  {tier.includes.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                      <CheckCircle className="w-4 h-4 text-[#5F8FB8] flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8">Common questions</h2>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div key={index} className="pb-6 border-b border-[#2E2E2E] last:border-0">
                <h3 className="font-semibold mb-2">{faq.q}</h3>
                <p className="text-sm text-[#9CA3AF] leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#242424] border-t border-[#2E2E2E]">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold mb-3">Need a quote?</h2>
          <p className="text-[#9CA3AF] mb-8 max-w-lg mx-auto">
            Get in touch to discuss your project. We'll give you a proper estimate 
            based on your specific requirements.
          </p>
          <Link 
            to="/contact" 
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => handlePricingCtaClick('general', 'Get in touch')}
          >
            Get in touch <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
